import { useState, useEffect, useRef, useMemo } from 'react';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { usePreset } from '@atlaskit/editor-core/use-preset';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { EditorActions } from '@atlaskit/editor-core';
import type { SyncBlockDataProviderInterface } from '@atlaskit/editor-synced-block-provider';

import { useMemoizedSyncedBlockProviderForEditor } from '@confluence/editor-features/entry-points/useSyncedBlockProvider.ts';
import { useActivityProvider } from '@confluence/editor-features/entry-points/useActivityProvider.ts';
import { useAutoformattingProvider } from '@confluence/editor-features/entry-points/useAutoformattingProvider.ts';
import { useContextIdentifierProvider } from '@confluence/editor-features/entry-points/useContextIdentifierProvider.ts';
import { useEditorFeatureFlags } from '@confluence/editor-features/entry-points/useEditorFeatureFlags.ts';
import { useMediaOptions } from '@confluence/editor-features/entry-points/useMediaOptions.ts';
import { useMentionProvider } from '@confluence/editor-features/entry-points/useMentionProvider.ts';
import { useSearchProvider } from '@confluence/editor-features/entry-points/useSearchProvider.ts';
import { getLayoutOptions } from '@confluence/editor-features/entry-points/getLayoutOptions.ts';
import { useExpandOptions } from '@confluence/editor-features/entry-points/useExpandOptions.ts';
import { useTableOptions } from '@confluence/editor-features/entry-points/useTableOptions.ts';
import { useLinkingFeatures } from '@confluence/editor-features/entry-points/useLinkingFeatures.ts';
import { useEditorAIOptIn } from '@confluence/editor-features/entry-points/useEditorAIOptIn.ts';
import { useEditorPluginAIOptions } from '@confluence/editor-features/entry-points/useEditorPluginAIOptions.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { createSmartCardEventHandler } from '@confluence/linking-platform/entry-points/createSmartCardEventHandler.ts';
import {
	useRouteActions,
	useMatchRoute,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useTrackChangesPlugin } from '@confluence/onboarding-ai-first-create/entry-points/useTrackChangesPluginOptions.ts';
import { getTaskDecisionProvider } from '@confluence/fabric-providers/entry-points/TaskDecisionProvider.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';

import { useEditorPreferencesProvider } from '../utils/EditorPreferences.ts';
import { createSnippetEditorPreset } from './createSnippetEditorPreset.ts';
import type { CreateSnippetEditorPresetOptions } from './createSnippetEditorPreset.ts';

const ANALYTICS_SOURCE = 'create-snippet-editor';
const EDITOR_APPEARANCE = 'full-page';

type UseSnippetEditorPresetProps = {
	placeholder: string;
	flags?: FlagsStateContainer;
	/** Content ID of the current page (for media upload tokens) */
	contentId: string;
	/** Space ID of the current page (for mentions, context identifier) */
	spaceId: string;
	/** Space key of the current page (for autoformatting) */
	spaceKey: string;
	/** EditorActions instance — required for bodied sync block support */
	editorActions: EditorActions;
};

type SnippetEditorPresetResult = {
	preset: ReturnType<typeof createSnippetEditorPreset>;
	editorApi: ExtractPresetAPI<ReturnType<typeof createSnippetEditorPreset>> | undefined;
} & {
	mentionProvider: ReturnType<typeof useMentionProvider>;
	contextIdentifierProvider: ReturnType<typeof useContextIdentifierProvider>;
	activityProvider: ReturnType<typeof useActivityProvider>;
	searchProvider: ReturnType<typeof useSearchProvider>;
	featureFlags: ReturnType<typeof useEditorFeatureFlags>;
};

type SnippetEditorPresetOptionsResult = {
	smartCreateProps: Omit<
		CreateSnippetEditorPresetOptions,
		'trackChangesPluginOptions' | 'editorPreferencesProvider'
	>;
	extra: Pick<
		SnippetEditorPresetResult,
		| 'mentionProvider'
		| 'contextIdentifierProvider'
		| 'activityProvider'
		| 'searchProvider'
		| 'featureFlags'
	>;
	setEditorAPIForMedia: (api: Parameters<typeof useMediaOptions>[0]['editorAPI']) => void;
};

/**
 * Builds all provider and plugin options for the snippet editor preset.
 * Extracted so useSnippetEditorPreset can follow the same thin "options → preset → return" pattern as useEmbeddedEditorPreset.
 */
function useSnippetEditorPresetOptions({
	placeholder,
	flags,
	contentId,
	spaceId,
	spaceKey,
	editorActions,
}: UseSnippetEditorPresetProps): SnippetEditorPresetOptionsResult {
	const { cloudId, userId, isAdminHubAIEnabled, edition, orgId, environment } = useSessionData();
	const [editorAPIForMedia, setEditorAPIForMedia] =
		useState<Parameters<typeof useMediaOptions>[0]['editorAPI']>(undefined);

	const mediaOptions = useMediaOptions({
		contentId: contentId ?? '',
		contentType: 'page',
		flags,
		editorAPI: editorAPIForMedia,
	});

	const mentionProvider = useMentionProvider(spaceId);
	const contextIdentifierProvider = useContextIdentifierProvider(spaceId, contentId);
	const autoformattingProvider = useAutoformattingProvider({
		contentId,
		spaceKey,
		source: ANALYTICS_SOURCE,
	});

	const tableOptions = useTableOptions();
	const expandOptions = useExpandOptions();
	const layoutOptions = getLayoutOptions();
	const searchProvider = useSearchProvider();
	const taskDecisionProvider = getTaskDecisionProvider();
	const emojiProvider = getEmojiProvider(EmojiResource, {
		cloudId,
		userId: userId || undefined,
	});
	const activityProvider = useActivityProvider();
	const linkingFeatures = useLinkingFeatures({ allowDatasource: true });

	const featureFlags = useEditorFeatureFlags();

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const { optInStatus, triggerOptInFlow } = useEditorAIOptIn({
		isAdminHubAIEnabled,
		isAIEnabledForContent,
		edition,
		orgId,
		environment,
	});
	const editorPluginAIOptions = useEditorPluginAIOptions({
		aiGlobalOptIn: { status: optInStatus, triggerOptInFlow },
		contentId,
	});

	const syncedBlockProvider = useMemoizedSyncedBlockProviderForEditor({
		contentId,
		contentType: 'page',
		focusedMentionId: undefined,
		spaceId,
		editorActions,
	}) as SyncBlockDataProviderInterface;

	const { push } = useRouteActions();
	const matchRoute = useMatchRoute();
	const syncedBlockEventHandlers = useMemo(
		() => createSmartCardEventHandler({ matchRoute, push }, true),
		[matchRoute, push],
	);

	const smartCreateProps: Omit<
		CreateSnippetEditorPresetOptions,
		'trackChangesPluginOptions' | 'editorPreferencesProvider'
	> = {
		appearance: EDITOR_APPEARANCE,
		media: mediaOptions,
		placeholder,
		tableOptions,
		expandOptions,
		layoutOptions,
		emojiProvider,
		mentionProvider,
		featureFlags,
		linking: linkingFeatures.linking,
		autoformattingProvider,
		taskDecisionProvider,
		contextIdentifierProvider,
		editorPluginAIOptions,
		syncedBlockProvider,
		syncedBlockEventHandlers,
	};

	return {
		smartCreateProps,
		extra: {
			mentionProvider,
			contextIdentifierProvider,
			activityProvider,
			searchProvider,
			featureFlags,
		},
		setEditorAPIForMedia,
	};
}

/**
 * Preset configuration for the snippet editor (e.g. Create Snippet modal).
 * Follows the same pattern as useEmbeddedEditorPreset: gather options → create preset via usePreset → return.
 * Uses createSnippetEditorPreset (no dependency on useSmartCreateEditorPreset).
 */
export function useSnippetEditorPreset({
	placeholder,
	flags,
	contentId,
	spaceId,
	spaceKey,
	editorActions,
}: UseSnippetEditorPresetProps): SnippetEditorPresetResult {
	const options = useSnippetEditorPresetOptions({
		placeholder,
		flags,
		contentId,
		spaceId,
		spaceKey,
		editorActions,
	});
	const { editorPreferencesProvider } = useEditorPreferencesProvider();
	const editorAPIRef = useRef<
		ExtractPresetAPI<ReturnType<typeof createSnippetEditorPreset>> | null | undefined
	>(null);
	const { pluginOptions: trackChangesPluginOptions } = useTrackChangesPlugin({ editorAPIRef });

	const { preset, editorApi } = usePreset(() => {
		return createSnippetEditorPreset({
			...options.smartCreateProps,
			trackChangesPluginOptions,
			editorPreferencesProvider,
		});
		// WARNING: Adding a dependency array here causes the editor plugins to all recreate
		// This can be very expensive. Be very careful adding to this, avoid if possible!
	});

	useEffect(() => {
		editorAPIRef.current = editorApi;
	}, [editorApi]);

	// Sync editor API to useMediaOptions so the media provider is set on the plugin when ready.
	// We use a ref for the setter so this effect only depends on editorApi; adding `options` would
	// re-run every render since options is a new object each time.
	const setEditorAPIForMediaRef = useRef(options.setEditorAPIForMedia);
	setEditorAPIForMediaRef.current = options.setEditorAPIForMedia;
	useEffect(() => {
		if (editorApi) {
			setEditorAPIForMediaRef.current(editorApi);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- options.setEditorAPIForMedia is stable (useState setter); options object is unstable
	}, [editorApi]);

	return {
		preset,
		editorApi,
		...options.extra,
	};
}
